// interlinked-tdd: exempt -- presentational only; the citation-extraction logic
// it renders is unit-tested in src/lib/citations.test.ts.
//
// Renders the verifiable provenance behind an assistant message: each upstream
// source (with license), the tool that produced it, when it was retrieved, and
// a short sha256 of the result bytes (full hash in the tooltip) so a reader can
// re-verify the cited data. This is the user-facing payoff of the fleet-wide
// `_meta.citation` provenance backend.
import type { Citation } from "@/lib/citations";

function shortTime(iso: string): string {
  // "2026-06-21T13:04:15.473Z" → "2026-06-21 13:04"
  return typeof iso === "string" && iso.length >= 16
    ? iso.slice(0, 16).replace("T", " ")
    : iso;
}

export function SourcesStrip({ citations }: { citations: Citation[] }) {
  if (!citations || citations.length === 0) return null;

  return (
    <details
      data-testid="sources-strip"
      className="mt-1 rounded bg-background/40 p-2 text-xs"
    >
      <summary className="cursor-pointer font-medium text-muted-foreground">
        🔗 Sources ({citations.length}) · verifiable
      </summary>
      <ul className="mt-1.5 space-y-1.5">
        {citations.map((c, i) => (
          <li
            key={c.result_hash || i}
            className="flex flex-wrap items-center gap-x-2 gap-y-0.5"
          >
            {c.source.url ? (
              <a
                href={c.source.url}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-emerald-600 underline-offset-2 hover:underline"
              >
                {c.source.name}
              </a>
            ) : (
              <span className="font-medium">{c.source.name}</span>
            )}
            {c.source.license && (
              <span
                className="rounded bg-muted/60 px-1 py-0.5 text-[10px] text-muted-foreground"
                title="Data license"
              >
                {c.source.license}
              </span>
            )}
            <span className="text-muted-foreground">{c.tool}</span>
            {typeof c.record_count === "number" && (
              <span className="text-muted-foreground">· {c.record_count} rec</span>
            )}
            <span className="text-muted-foreground">· {shortTime(c.retrieved_at)}</span>
            <span
              className="font-mono text-[10px] text-muted-foreground/80"
              title={`Content hash (re-verifiable): ${c.result_hash}`}
            >
              sha256:{c.result_hash.slice(0, 12)}
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}
